import cn from 'classnames';
import Link from 'next/link';
import { Container, HoverLine, Social } from '.';
import {
  CONTACT_EMAIL,
  CONTACT_EMAIL_HREF,
  CONTACT_PHONE,
  CONTACT_PHONE_HREF,
} from '../utils/contacts';
import styles from './footer.module.css';
import Logo, { type } from './logo';
import { SocialVariant } from './social';

interface FooterProps {
  variant?: 'main' | 'letslink' | 'fitkonnekt' | 'keto' | 'healthCase';
  logo?: type;
  line?: 'letslink' | 'white' | 'keto' | undefined;
}

export default function Footer({
  variant = 'main',
  logo = 'text',
  line,
}: FooterProps) {
  return (
    <footer
      className={cn(styles.footer, styles[variant])}
      itemScope
      itemType="http://schema.org/Organization"
    >
      <link itemProp="logo" href="https://mavinx.com/logo/logo.svg" />
      <Container>
        <div className={styles.footer__start}>
          <Logo className={styles.logo} appearance={logo} />
          <nav className={styles.footer__nav}>
            <ul className={styles.menu__list}>
              <li className={styles['menu__list-item']}>
                <HoverLine appearance={line}>
                  <Link href="/#works" passHref prefetch={false}>
                    <a className={styles['menu__list-link']}> Works </a>
                  </Link>
                </HoverLine>
              </li>
              <li className={styles['menu__list-item']}>
                <HoverLine appearance={line}>
                  <Link href="/real-estate" passHref prefetch={false}>
                    <a className={styles['menu__list-link']}>Real Estate</a>
                  </Link>
                </HoverLine>
              </li>
              <li className={styles['menu__list-item']}>
                <HoverLine appearance={line}>
                  <Link href="/#services" passHref prefetch={false}>
                    <a className={styles['menu__list-link']}>Services</a>
                  </Link>
                </HoverLine>
              </li>
              <li className={styles['menu__list-item']}>
                <HoverLine appearance={line}>
                  <Link href="/team" passHref prefetch={false}>
                    <a className={styles['menu__list-link']}>Team</a>
                  </Link>
                </HoverLine>
              </li>
            </ul>
          </nav>
        </div>
        <div className={styles.footer__middle}>
          <HoverLine appearance={line}>
            <a
              className={styles.footer__email}
              href={CONTACT_EMAIL_HREF}
              itemProp="email"
              rel="noopener noreferrer"
            >
              {CONTACT_EMAIL}
            </a>
          </HoverLine>
          <HoverLine appearance={line}>
            <a
              className={styles.footer__phone}
              itemProp="telephone"
              href={CONTACT_PHONE_HREF}
              rel="noopener noreferrer"
            >
              {CONTACT_PHONE}
            </a>
          </HoverLine>
        </div>
        <div className={styles.footer__end}>
          <div className={styles.footer__rights}>
            (c) 2026. Mavinx. All rights reserved
          </div>
          <div className={styles.links}>
            <a
              className={styles.linkMap}
              href="https://goo.gl/maps/EiJ7PAEBnBj7CjHd8"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg
                width="18"
                height="24"
                viewBox="0 0 18 24"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9 24C10.9839 24 18 13.5076 18 8.70175C18 3.89591 13.9706 0 9 0C4.02944 0 0 3.89591 0 8.70175C0 13.5076 7.1129 24 9 24ZM8.95161 13.193C11.4903 13.193 13.5484 11.2031 13.5484 8.74854C13.5484 6.29394 11.4903 4.30409 8.95161 4.30409C6.41288 4.30409 4.35484 6.29394 4.35484 8.74854C4.35484 11.2031 6.41288 13.193 8.95161 13.193Z"
                />
              </svg>
              <span itemProp="address">Ukraine, Dnipro</span>
            </a>
            <a
              className={styles.linkMap}
              href="https://goo.gl/maps/i3mCcQXvcntBXkbA8"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg
                width="18"
                height="24"
                viewBox="0 0 18 24"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9 24C10.9839 24 18 13.5076 18 8.70175C18 3.89591 13.9706 0 9 0C4.02944 0 0 3.89591 0 8.70175C0 13.5076 7.1129 24 9 24ZM8.95161 13.193C11.4903 13.193 13.5484 11.2031 13.5484 8.74854C13.5484 6.29394 11.4903 4.30409 8.95161 4.30409C6.41288 4.30409 4.35484 6.29394 4.35484 8.74854C4.35484 11.2031 6.41288 13.193 8.95161 13.193Z"
                />
              </svg>
              <span itemProp="address">Poland, Wroclaw</span>
            </a>
          </div>
          <div className={styles.footer__social}>
            <Social
              variant={SocialVariant.instagram}
              className={styles.footer__icon}
            />
            <Social
              variant={SocialVariant.dribble}
              className={styles.footer__icon}
            />
            <Social
              variant={SocialVariant.behance}
              className={styles.footer__icon}
            />
            <Social
              variant={SocialVariant.clutch}
              className={styles.footer__icon}
            />
            <Social
              variant={SocialVariant.linkedin}
              className={styles.footer__icon}
            />
          </div>
        </div>
      </Container>
    </footer>
  );
}
